// B-60 — the state behind "Procesar el día" (`ProcesoDelDia.jsx`, inside "Nuevo cierre"): the date,
// the chosen files, the send, and the progress poll. Out of the component so it can be rendered in
// a probe and tested without Radix (see `__tests__/ConciliacionDia.test.jsx`); the decisions
// themselves are pure and live in `utils/cierreDiaria.js`.
//
// The poll is a chained `setTimeout`, not an interval: a request that takes longer than the period
// would otherwise stack a second one behind it. It asks again only while the backend says the run
// is `running` — the daily lock, so the answer is the same whichever API task serves it — and the
// cleanup stops it when the client or the date changes or the screen unmounts.
//
// A check that fails while a run is live does NOT stop the poll (the E2E test of 29/09/2026 found
// that it did: one failed check froze the screen on "23 de 140" for the rest of a run that finished
// fine). The run is on the server and keeps going; the screen says it is retrying and asks again.
// Only a failure before anything was ever shown is an error, as before.
//
// B-80 — a day processed before is redone, and the backend wants that said (`redo`). `procesado`
// tells the screen to confirm first; a 409 `DAY_ALREADY_PROCESSED` (the day was processed by someone
// else meanwhile) sets `pideConfirmar`, so the screen confirms then. The moment a run is sent the
// counters go to zero (`avanceAlEnviar`): the ones the day had are the last run's.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getCierreDailyProgress, runCierreDailyProcess } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import {
  avanceAlEnviar,
  clavesDiarias,
  clavesObligatorias,
  diaProcesado,
  errorDeProceso,
  fechaPorDefecto,
  puedeProcesar,
  resumenDeAvance,
} from "../../../utils/cierreDiaria";

export const INTERVALO_AVANCE_MS = 5000;
// How many answers after a send may still say "not running" before the run is given up on. The
// backend answers 202 and takes the daily lock a moment later, so the first progress asked right
// after the send can predate the run; without this the poll stopped there and the run went unseen.
export const ESPERAS_DE_ARRANQUE = 12;

export function useConciliacionDia({ token, mantisBusinessId, clientCode, timezone, listo }) {
  const [fecha, setFecha] = useState("");
  const [archivos, setArchivos] = useState({});
  const [progreso, setProgreso] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState("");
  const [enviado, setEnviado] = useState(false);
  // Sent, and no progress read since: the run has the files but the screen has not heard from it.
  // Without it the files vanish from their slots and nothing says they arrived (E2E, 30/09/2026).
  const [esperandoAvance, setEsperandoAvance] = useState(false);
  // A check failed while a run was live; the next one is already scheduled.
  const [reintentando, setReintentando] = useState(false);
  // Whether the last progress read said the run was going — what decides a failed check is retried.
  const enCurso = useRef(false);
  // Bumped after a send: restarts the poll, and remounts the file inputs so they read empty.
  const [ronda, setRonda] = useState(0);
  // Between a send and the first answer that shows the run (see ESPERAS_DE_ARRANQUE).
  const [arrancando, setArrancando] = useState(false);
  const esperasDeArranque = useRef(0);
  // B-80 — the backend refused because the day was already processed: confirm, then send `redo`.
  const [pideConfirmar, setPideConfirmar] = useState(false);

  // A new client opens on its own yesterday. Only a new client: `listo` also goes off and on when
  // "Nuevo cierre" closes and its progress dialog reopens, and that must not move the day being
  // followed back to yesterday.
  const fechaDelCliente = useRef(null);
  useEffect(() => {
    if (!listo) return;
    const clave = `${clientCode}|${timezone || ""}`;
    if (fechaDelCliente.current === clave) return;
    fechaDelCliente.current = clave;
    setFecha(fechaPorDefecto(new Date(), timezone));
  }, [listo, clientCode, timezone]);

  // Files chosen for one day are never sent for another, and another day's counts are not shown
  // while this one's load.
  useEffect(() => {
    setProgreso(null);
    setArchivos({});
    setErrorEnvio("");
    setEnviado(false);
    setEsperandoAvance(false);
    setReintentando(false);
    enCurso.current = false;
    setArrancando(false);
    esperasDeArranque.current = 0;
    setPideConfirmar(false);
  }, [clientCode, fecha]);

  useEffect(() => {
    if (!listo || !fecha || !mantisBusinessId) return undefined;
    let activo = true;
    let espera = null;

    const consultar = async () => {
      const response = await getCierreDailyProgress(token, mantisBusinessId, clientCode, fecha).catch(() => null);
      if (!activo) return;
      setCargando(false);
      if (!response?.success) {
        if (enCurso.current) {
          setReintentando(true);
          espera = setTimeout(consultar, INTERVALO_AVANCE_MS);
          return;
        }
        setErrorCarga(correctionError(response));
        return;
      }
      setErrorCarga("");
      setReintentando(false);
      setEsperandoAvance(false);
      // B-80 — between the send and the run showing, the counters are the previous run's: a redo
      // would read 100%. Until the run is live, nothing is counted.
      const esperando = !response.data?.running && esperasDeArranque.current > 1;
      setProgreso(esperando ? avanceAlEnviar(response.data) : response.data);
      if (response.data?.running || esperasDeArranque.current <= 1) {
        esperasDeArranque.current = 0;
        setArrancando(false);
      } else {
        esperasDeArranque.current -= 1;
      }
      // Still live while it runs, or while a send is waiting for the run to show.
      enCurso.current = Boolean(response.data?.running) || esperasDeArranque.current > 0;
      if (enCurso.current) espera = setTimeout(consultar, INTERVALO_AVANCE_MS);
    };

    setCargando(true);
    consultar();
    return () => {
      activo = false;
      clearTimeout(espera);
    };
  }, [token, mantisBusinessId, clientCode, fecha, listo, ronda]);

  const claves = useMemo(() => clavesDiarias(progreso), [progreso]);
  const obligatorias = useMemo(() => clavesObligatorias(progreso), [progreso]);
  const avance = useMemo(() => resumenDeAvance(progreso), [progreso]);
  const corriendo = Boolean(progreso?.running) || arrancando;
  const procesado = useMemo(() => diaProcesado(progreso), [progreso]);
  // B-101 — only the reports every office shares are sent here; an office's own documents reach its
  // closing by its email (`email/store.js`), never with the day.
  const listoParaProcesar = !enviando && !corriendo && !!progreso && puedeProcesar(archivos, obligatorias, { procesado });

  const elegirArchivo = useCallback((clave, file) => {
    setErrorEnvio("");
    setArchivos((actual) => ({ ...actual, [clave]: file || null }));
  }, []);

  // Answers whether there is now a run to follow: this one, or one somebody else started (409).
  const procesar = useCallback(async ({ redo = false } = {}) => {
    setErrorEnvio("");
    setEnviado(false);
    setPideConfirmar(false);
    setEnviando(true);
    const response = await runCierreDailyProcess(token, mantisBusinessId, clientCode, fecha, archivos, { redo }).catch(() => null);
    setEnviando(false);
    if (!response?.success) {
      // Processed by someone else since the screen loaded: confirm first, then send it as a redo.
      if (response?.errorCode === "DAY_ALREADY_PROCESSED") {
        setPideConfirmar(true);
        return false;
      }
      setErrorEnvio(errorDeProceso(response));
      // A run somebody else started is still worth following.
      if (response?.errorCode === "CONFLICT") {
        setRonda((r) => r + 1);
        return true;
      }
      return false;
    }
    setArchivos({});
    setEnviado(true);
    setEsperandoAvance(true);
    setProgreso((actual) => avanceAlEnviar(actual));
    // The backend answered 202: the run is live (even before it shows as `running`), so a failed
    // first check is retried rather than reported.
    enCurso.current = true;
    esperasDeArranque.current = ESPERAS_DE_ARRANQUE;
    setArrancando(true);
    setRonda((r) => r + 1);
    return true;
  }, [token, mantisBusinessId, clientCode, fecha, archivos]);

  return {
    fecha,
    setFecha,
    archivos,
    elegirArchivo,
    claves,
    obligatorias,
    progreso,
    avance,
    corriendo,
    cargando,
    errorCarga,
    enviando,
    errorEnvio,
    enviado,
    esperandoAvance,
    reintentando,
    ronda,
    listoParaProcesar,
    procesar,
    procesado,
    pideConfirmar,
    setPideConfirmar,
  };
}
